import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '@relay-towing/tokens';
import {
  avatarFallbackRecipe,
  avatarGroupRecipe,
  avatarImageRecipe,
  avatarRecipe,
  scrollAreaRecipe,
  scrollBarRecipe,
  scrollThumbRecipe,
  scrollViewportRecipe,
  type AvatarRecipeProps,
} from '../recipes/media.recipe';

type AvatarSize = NonNullable<AvatarRecipeProps['size']>;
type AvatarShape = NonNullable<AvatarRecipeProps['shape']>;
type AvatarTone = 'muted' | 'primary' | 'outline';

/**
 * Turns a name into initials for the fallback.
 *
 * Takes the first and last word, so "Marcus J. Webb" gives MW rather than MJ.
 * Falls back to a single character, then to nothing — never to a placeholder
 * glyph, which reads as an error rather than an absence.
 */
function initialsFrom(name?: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    AvatarRecipeProps {
  src?: string | null;
  /** Used for the alt text and to derive the initials. */
  name?: string;
  /** Override the derived initials. */
  fallback?: string;
  /** Emphasis on the initials when there is no image. */
  tone?: AvatarTone;
}

/**
 * A person or company.
 *
 * Most drivers have no photo, so the initials fallback is the normal case
 * rather than an error state — Radix swaps to it automatically if the image
 * fails or is absent.
 */
export const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(function Avatar(
  {
    className,
    shape = 'circle',
    size = 'md',
    ringed,
    tone = 'muted',
    src,
    name,
    fallback,
    children,
    ...props
  },
  ref
) {
  const initials = fallback ?? initialsFrom(name);

  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(avatarRecipe({ shape, size, ringed }), className)}
      {...props}
    >
      {/* Composed: <Avatar><AvatarImage/><AvatarFallback/></Avatar>. The
          `src`/`name` props are shorter and derive the initials for you, so
          prefer those; children win when both are given. */}
      {children ?? (
        <>
          {src ? (
            <AvatarPrimitive.Image
              src={src}
              alt={name ?? ''}
              className={avatarImageRecipe()}
            />
          ) : null}
          <AvatarPrimitive.Fallback
            /* cn, not the bare recipe: a compound variant adds a second
               `rounded-*` and only tailwind-merge resolves which one wins. */
            className={cn(
              avatarFallbackRecipe({ shape, tone, size: size as AvatarSize })
            )}
            /*
             * With no image there is nothing to wait for, so the initials render
             * on the first paint — `undefined`, not 0. Any number makes Radix
             * start hidden and flip after a timer, which shows an empty circle for
             * a frame on every avatar without a photo (most of them).
             *
             * With an image, a short delay avoids initials flashing before a
             * cached photo appears.
             */
            delayMs={src ? 300 : undefined}
          >
            {initials}
          </AvatarPrimitive.Fallback>
        </>
      )}
    </AvatarPrimitive.Root>
  );
});

export const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(function AvatarImage({ className, ...props }, ref) {
  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn(avatarImageRecipe(), className)}
      {...props}
    />
  );
});

export interface AvatarFallbackProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
  size?: AvatarSize;
  shape?: AvatarShape;
  tone?: AvatarTone;
}

export const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(function AvatarFallback(
  {
    className,
    size = 'md',
    shape = 'circle',
    tone = 'muted',
    delayMs,
    ...props
  },
  ref
) {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(avatarFallbackRecipe({ shape, tone, size }), className)}
      // See the note above: a delay on an avatar with no image shows an empty
      // circle for a frame.
      delayMs={delayMs}
      {...props}
    />
  );
});

export interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  orientation?: 'vertical' | 'horizontal';
}

/**
 * A scrollable region with a styled scrollbar.
 *
 * Worth using where a native scrollbar would be visually loud — side panels,
 * long menus. Plain `overflow-auto` is fine everywhere else; this adds a
 * wrapper element and is not free.
 */
export const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(function ScrollArea(
  { className, children, orientation = 'vertical', ...props },
  ref
) {
  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn(scrollAreaRecipe(), className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className={scrollViewportRecipe({ orientation })}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollAreaPrimitive.ScrollAreaScrollbar
        orientation={orientation}
        className={scrollBarRecipe({ orientation })}
      >
        <ScrollAreaPrimitive.ScrollAreaThumb className={scrollThumbRecipe()} />
      </ScrollAreaPrimitive.ScrollAreaScrollbar>
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
